import { useCallback, useEffect } from 'react';
import type {MouseEvent, ReactNode} from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@/fontawesome';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  icon?: ReactNode;
  maxWidth?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl' | '7xl' | 'screen' | 'fullscreen';
  maxHeight?: string;
  children: ReactNode;
  closeDisabled?: boolean;
  /** ESC schliesst das Modal - bewusst opt-in, damit bestehende Formular-Modals unveraendert bleiben */
  closeOnEscape?: boolean;
  /** Klick auf den Hintergrund schliesst das Modal - ebenfalls opt-in (Fehlklick koennte Eingaben verwerfen) */
  closeOnBackdropClick?: boolean;
}

export default function Modal({
  isOpen,
  onClose,
  title,
  icon,
  maxWidth = 'lg',
  maxHeight = '90vh',
  children,
  closeDisabled = false,
  closeOnEscape = false,
  closeOnBackdropClick = false
}: ModalProps) {
  const handleClose = useCallback(() => {
    if (!closeDisabled) {
      onClose();
    }
  }, [closeDisabled, onClose]);

  useEffect(() => {
    if (!isOpen || !closeOnEscape) {
      return;
    }

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => { document.removeEventListener('keydown', handleKeyDown); };
  }, [isOpen, closeOnEscape, handleClose]);

  // Muss nach den Hooks stehen, sonst wuerden diese bedingt aufgerufen (Rules of Hooks)
  if (!isOpen) return null;

  const maxWidthClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    '2xl': 'max-w-2xl',
    '3xl': 'max-w-3xl',
    '4xl': 'max-w-4xl',
    '5xl': 'max-w-5xl',
    '6xl': 'max-w-6xl',
    '7xl': 'max-w-7xl',
    'screen': 'max-w-screen-xl',
    'fullscreen': 'w-full h-full max-w-none'
  };

  // Nur schliessen, wenn wirklich der Hintergrund getroffen wurde - Klicks in der Karte
  // duerfen nicht durchblubbern
  const handleBackdropClick = (e: MouseEvent<HTMLDivElement>) => {
    if (closeOnBackdropClick && e.target === e.currentTarget) {
      handleClose();
    }
  };

  const isFullscreen = maxWidth === 'fullscreen';

  // Render via a portal to document.body so the modal escapes any ancestor
  // stacking context (e.g. cards with `relative z-*` or swipe transforms) and
  // is always painted above the rest of the page.
  return createPortal(
    <div
      onClick={handleBackdropClick}
      className={`fixed inset-0 bg-black/50 flex items-center justify-center z-50 ${isFullscreen ? 'p-4' : 'p-4'}`}
    >
      <div
        className={`bg-white dark:bg-gray-800 rounded-lg shadow-xl overflow-hidden ${
          isFullscreen 
            ? 'w-full h-full max-w-none flex flex-col' 
            : `${maxWidthClasses[maxWidth]} w-full`
        }`}
        style={isFullscreen ? { height: 'calc(100vh - 2rem)', width: 'calc(100vw - 2rem)' } : { maxHeight }}
      >
        <div className="flex items-center justify-between gap-3 p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center min-w-0">
            {icon && <div className="mr-3 shrink-0">{icon}</div>}
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
              {title}
            </h2>
          </div>
          <button
            onClick={handleClose}
            disabled={closeDisabled}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50 cursor-pointer shrink-0"
          >
            <FontAwesomeIcon icon={faXmark} className="w-5 h-5" />
          </button>
        </div>
        <div
          className={isFullscreen ? "flex-1 min-h-0" : "overflow-y-auto"}
          style={
            isFullscreen
              ? { height: 'calc(100vh - 2rem - 100px)' }
              : { maxHeight: `calc(${maxHeight} - 100px)` }
          }
        >
          {children}
        </div>
      </div>
    </div>,
    document.body
  );
}
